import React, { useRef, useEffect, useCallback, useState, useMemo } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { sqMetersToSqft } from "./polygonArea";
import LawnMowerIcon from "./LawnMowerIcon";

const defaultContainerStyle = {
  width: "100%",
  height: "min(440px, 58vh)",
};

const MIN_POINT_GAP_M = 1.1;
const MIN_POINTS_FOR_POLYGON = 3;

function createProjectionOverlay(map) {
  const overlay = new window.google.maps.OverlayView();
  overlay.onAdd = function () {};
  overlay.draw = function () {};
  overlay.setMap(map);
  return overlay;
}

function clientPoint(e) {
  if (e.touches && e.touches[0]) {
    return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
  }
  if (e.changedTouches && e.changedTouches[0]) {
    return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
  }
  return { clientX: e.clientX, clientY: e.clientY };
}

function latLngFromEvent(map, overlay, e) {
  const projection = overlay.getProjection();
  if (!projection || !map) return null;
  const { clientX, clientY } = clientPoint(e);
  const rect = map.getDiv().getBoundingClientRect();
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null;
  return projection.fromContainerPixelToLatLng(new window.google.maps.Point(x, y));
}

function pathLiteralsFromGooglePath(path) {
  const n = path.getLength();
  const out = [];
  for (let i = 0; i < n; i++) {
    const p = path.getAt(i);
    out.push({ lat: p.lat(), lng: p.lng() });
  }
  return out;
}

/**
 * Satellite map: lawnmower "paintbrush" freehand outline + editable polygon area.
 * @param {Array<{lat:number,lng:number}>|null|undefined} savedOutlinePath — restored when switching parcels (no re-apply while the same polygon is already on the map).
 */
function Map({ location, onDrawnAreaChange, resetDrawingSignal = 0, savedOutlinePath = null }) {
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const polygonRef = useRef(null);
  const polylineRef = useRef(null);
  const pathListenersRef = useRef([]);

  const pointsRef = useRef([]);
  const [mapReady, setMapReady] = useState(false);
  const [paintArmed, setPaintArmed] = useState(false);
  const [stroking, setStroking] = useState(false);

  const paintArmedRef = useRef(false);
  const strokingRef = useRef(false);
  useEffect(() => {
    paintArmedRef.current = paintArmed;
  }, [paintArmed]);
  useEffect(() => {
    strokingRef.current = stroking;
  }, [stroking]);

  const clearPathListeners = useCallback(() => {
    pathListenersRef.current.forEach((l) => l?.remove?.());
    pathListenersRef.current = [];
  }, []);

  const teardownDrawing = useCallback(() => {
    clearPathListeners();
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    pointsRef.current = [];
  }, [clearPathListeners]);

  const computeSqftFromPolygon = useCallback((polygon) => {
    const path = polygon.getPath();
    const sqm = window.google.maps.geometry.spherical.computeArea(path);
    return sqMetersToSqft(sqm);
  }, []);

  const attachPolygonPathListeners = useCallback(
    (polygon) => {
      clearPathListeners();
      const path = polygon.getPath();
      const update = () => {
        try {
          const sqft = computeSqftFromPolygon(polygon);
          const path = polygon.getPath();
          const literals = pathLiteralsFromGooglePath(path);
          if (sqft > 0) onDrawnAreaChange?.(sqft, { path: literals });
        } catch (err) {
          console.warn(err);
        }
      };
      ["insert_at", "remove_at", "set_at"].forEach((evt) => {
        pathListenersRef.current.push(path.addListener(evt, update));
      });
    },
    [clearPathListeners, computeSqftFromPolygon, onDrawnAreaChange]
  );

  const finalizeStroke = useCallback(() => {
    const map = mapRef.current;
    const pts = pointsRef.current.slice();
    pointsRef.current = [];

    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    if (pts.length < MIN_POINTS_FOR_POLYGON) {
      setStroking(false);
      return;
    }

    teardownDrawing();
    const polygon = new window.google.maps.Polygon({
      map,
      paths: pts,
      fillColor: "#2d6a4f",
      fillOpacity: 0.22,
      strokeColor: "#2e7d32",
      strokeWeight: 3,
      editable: true,
      draggable: false,
      zIndex: 1,
    });
    polygonRef.current = polygon;
    attachPolygonPathListeners(polygon);
    const sqft = computeSqftFromPolygon(polygon);
    const literals = pts.map((p) => ({ lat: p.lat(), lng: p.lng() }));
    onDrawnAreaChange?.(sqft > 0 ? sqft : null, { path: sqft > 0 ? literals : null });
    setStroking(false);
    setPaintArmed(false);
  }, [attachPolygonPathListeners, computeSqftFromPolygon, onDrawnAreaChange, teardownDrawing]);

  const appendPoint = useCallback(
    (latLng) => {
      if (!latLng) return;
      const pts = pointsRef.current;
      const last = pts[pts.length - 1];
      if (last) {
        const d = window.google.maps.geometry.spherical.computeDistanceBetween(last, latLng);
        if (d < MIN_POINT_GAP_M) return;
      }
      pts.push(latLng);
      if (polylineRef.current) {
        polylineRef.current.setPath(pts);
      }
    },
    []
  );

  const startStroke = useCallback(
    (e) => {
      if (!paintArmedRef.current) return;
      const map = mapRef.current;
      const overlay = overlayRef.current;
      if (!map || !overlay) return;

      e.preventDefault?.();
      e.stopPropagation?.();
      const latLng = latLngFromEvent(map, overlay, e);
      if (!latLng) return;

      teardownDrawing();

      pointsRef.current = [latLng];
      polylineRef.current = new window.google.maps.Polyline({
        map,
        path: pointsRef.current,
        strokeColor: "#2e7d32",
        strokeOpacity: 1,
        strokeWeight: 5,
        zIndex: 2,
        geodesic: true,
      });
      setStroking(true);
    },
    [teardownDrawing]
  );

  useEffect(() => {
    if (!stroking) return undefined;

    const move = (e) => {
      if (e.cancelable && (e.type === "touchmove" || e.touches?.length)) {
        e.preventDefault();
      }
      const map = mapRef.current;
      const overlay = overlayRef.current;
      if (!map || !overlay) return;
      const latLng = latLngFromEvent(map, overlay, e);
      appendPoint(latLng);
    };

    const up = () => {
      finalizeStroke();
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [stroking, appendPoint, finalizeStroke]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return undefined;
    const div = map.getDiv();

    const down = (e) => {
      if (!paintArmedRef.current || strokingRef.current) return;
      startStroke(e);
    };

    div.addEventListener("mousedown", down, true);
    div.addEventListener("touchstart", down, { passive: false, capture: true });

    return () => {
      div.removeEventListener("mousedown", down, true);
      div.removeEventListener("touchstart", down, { capture: true });
    };
  }, [mapReady, paintArmed, startStroke]);

  const mapInteractionLocked = paintArmed || stroking;
  const mapOptions = useMemo(
    () => ({
      mapTypeId: "satellite",
      draggable: !mapInteractionLocked,
      gestureHandling: mapInteractionLocked ? "none" : "greedy",
      scrollwheel: !mapInteractionLocked,
      disableDoubleClickZoom: mapInteractionLocked,
    }),
    [mapInteractionLocked]
  );

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    map.setMapTypeId(window.google.maps.MapTypeId.SATELLITE);
    overlayRef.current = createProjectionOverlay(map);
    window.google.maps.event.addListenerOnce(map, "idle", () => {
      setMapReady(true);
    });
  }, []);

  const resetSkipRef = useRef(true);
  useEffect(() => {
    if (resetSkipRef.current) {
      resetSkipRef.current = false;
      return;
    }
    teardownDrawing();
    setPaintArmed(false);
    setStroking(false);
  }, [resetDrawingSignal, teardownDrawing]);

  useEffect(() => {
    if (!mapReady) return;
    if (!savedOutlinePath || savedOutlinePath.length < MIN_POINTS_FOR_POLYGON) return;
    if (polygonRef.current) return;

    const map = mapRef.current;
    if (!map) return;

    const pts = savedOutlinePath.map(
      (p) => new window.google.maps.LatLng(p.lat, p.lng)
    );
    const polygon = new window.google.maps.Polygon({
      map,
      paths: pts,
      fillColor: "#2d6a4f",
      fillOpacity: 0.22,
      strokeColor: "#2e7d32",
      strokeWeight: 3,
      editable: true,
      draggable: false,
      zIndex: 1,
    });
    polygonRef.current = polygon;
    attachPolygonPathListeners(polygon);
  }, [mapReady, savedOutlinePath, attachPolygonPathListeners]);

  return (
    <Box sx={{ position: "relative" }}>
      <GoogleMap
        mapContainerStyle={defaultContainerStyle}
        center={location}
        zoom={19}
        mapTypeId="satellite"
        onLoad={onMapLoad}
        options={mapOptions}
      >
        <Marker position={location} />
      </GoogleMap>

      <Tooltip
        title={
          paintArmed
            ? "Press and drag to paint the lawn edge. Map panning is off while the brush is on — tap the mower again to pan."
            : "Paintbrush mode: trace your lawn freehand"
        }
      >
        <IconButton
          aria-label="Draw lawn with paintbrush"
          onClick={() => setPaintArmed((a) => !a)}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 3,
            bgcolor: paintArmed ? "primary.main" : "rgba(255,255,255,0.92)",
            color: paintArmed ? "primary.contrastText" : "primary.main",
            boxShadow: 2,
            "&:hover": {
              bgcolor: paintArmed ? "primary.dark" : "rgba(255,255,255,1)",
            },
          }}
          size="large"
        >
          <LawnMowerIcon />
        </IconButton>
      </Tooltip>

      {paintArmed ? (
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            left: 8,
            right: 64,
            top: 10,
            zIndex: 2,
            bgcolor: "rgba(255,255,255,0.88)",
            px: 1,
            py: 0.5,
            borderRadius: 1,
            pointerEvents: "none",
            color: "text.secondary",
          }}
        >
          Brush on: drag to outline the lawn (map won't pan). Tap the mower again when you need to move the map.
        </Typography>
      ) : null}

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1,
          border: stroking ? "2px dashed rgba(46, 125, 50, 0.6)" : "none",
          borderRadius: 1,
        }}
      />
    </Box>
  );
}

export default Map;
